import { Box, Button, Dialog, DialogContent, Typography, makeStyles } from "@material-ui/core";
import React, { useMemo } from "react";

import { User } from "@/api/generated";

import CloudfundingCozuchiBannerImage from "@assets/image/cloudfaunding_cozuchi_banner.png";

const COZUCHI_HOME_URL = "https://cozuchi.com/ja/";
const COZUCHI_PRODUCTION_URL =
  "https://ad.laetoli.jp/link.php?i=phzd3920727n&m=mid1lt6wd0op&pbid=[cid]&kbp1=[p]";
const COZUCHI_PARTNER_PARAM = "affilicode_partner";

const useStyles = makeStyles((theme) => ({
  modalPaper: {
    borderRadius: 16,
    width: "calc(100% - 32px)",
    margin: theme.spacing(2),
  },
  modalContent: {
    padding: theme.spacing(2, 2.5),
    "&.MuiDialogContent-root:first-child": {
      paddingTop: theme.spacing(2),
    },
  },
  modalTitle: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 1.5,
    textAlign: "center",
    marginBottom: theme.spacing(1),
  },
  leadBox: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  leadText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  modalBody: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.7,
    marginBottom: theme.spacing(0.5),
  },
  textLink: {
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.7,
    textDecoration: "none",
  },
  referenceMark: {
    fontSize: 10,
    lineHeight: 1,
    verticalAlign: "super",
  },
  modalNote: {
    color: theme.palette.text.secondary,
    fontSize: 10,
    fontWeight: 400,
    lineHeight: 1.5,
    marginBottom: theme.spacing(1.5),
  },
  bannerLink: {
    display: "block",
    marginBottom: theme.spacing(1),
  },
  modalBanner: {
    display: "block",
    width: "100%",
  },
  closeButton: {
    display: "block",
    margin: theme.spacing(0, "auto"),
    color: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
  },
}));

const buildCozuchiUrl = (user: Pick<User, "identifier"> | null) => {
  if (!user?.identifier) return COZUCHI_HOME_URL;
  if (process.env.NODE_ENV !== "production") return `${COZUCHI_HOME_URL}?${encodeURIComponent(user.identifier)}`;

  const cid = `propally_${user.identifier}`;
  return COZUCHI_PRODUCTION_URL.replace("[cid]", encodeURIComponent(cid)).replace(
    "[p]",
    encodeURIComponent(COZUCHI_PARTNER_PARAM)
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  user: Pick<User, "identifier"> | null;
}

export const CozuchiModal: React.FC<Props> = ({ open, onClose, user }) => {
  const classes = useStyles();
  const cozuchiUrl = useMemo(() => buildCozuchiUrl(user), [user]);

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.modalPaper }}>
      <DialogContent className={classes.modalContent}>
        <Typography className={classes.modalTitle}>
          “ローンを使わない不動産投資”
          <br />
          という選択肢
        </Typography>
        <Box className={classes.leadBox}>
          <Typography className={classes.leadText}>
            1万円から始められる
            <br />
            「不動産投資クラウドファンディング」
          </Typography>
        </Box>
        <Typography className={classes.modalBody}>
          物件購入だけが、不動産投資ではありません。プロパリーの認定クラファン
          <a href={cozuchiUrl} target="_blank" rel="noopener noreferrer" className={classes.textLink}>
            COZUCHI（コヅチ）
          </a>
          は、不動産投資クラウドファンディング累計投資額No.1
          <span className={classes.referenceMark}>※1</span>
          。プロが厳選した都心の不動産に少額から投資でき、想定年利回りは4〜10%(税引前)、これまで元本割れゼロの実績
          <span className={classes.referenceMark}>※2</span>
          があります。
        </Typography>
        <Typography className={classes.modalNote}>
          ※1 不動産投資クラウドファンディングサービスにおける市場調査（2024年4月末時点／日本マーケティングリサーチ機構）
          <br />
          ※2 元本割れ実績は2026年4月末時点です。また、将来の運用成果を保証するものではありません。
        </Typography>
        <a href={cozuchiUrl} target="_blank" rel="noopener noreferrer" className={classes.bannerLink}>
          <img src={CloudfundingCozuchiBannerImage} alt="COZUCHIを見てみる" className={classes.modalBanner} />
        </a>
        <Button className={classes.closeButton} onClick={onClose}>
          閉じる
        </Button>
      </DialogContent>
    </Dialog>
  );
};
